import React, { useState } from "react";
import { Button, Spinner, Switch, Table } from "@optimizely/axiom";
import { datadogRum } from "@datadog/browser-rum";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import { VendorNameModal } from "../DeveloperSettings/VendorNameModal";
import { emitToast } from "../../../lib/toaster-utils";
import { UNEXPECTED_ERROR } from "../../../constants/constants";

type DeveloperSettingsTableRowProps = {
    organizationId: string;
    organizationName: string;
    enabled: boolean;
    vendorName: string;
    loading?: boolean;
    onUpdate: (payload: { enabled: boolean; vendorName: string }) => Promise<unknown>;
};

type ActiveDialog = "set-vendor" | "edit-vendor" | "disable-confirm" | null;

const getErrorMessage = (err: unknown): string =>
    (err as { message?: string })?.message || (Array.isArray(err) && err[0]?.message) || UNEXPECTED_ERROR;

export const DeveloperSettingsTableRow = ({
    organizationId,
    organizationName,
    enabled,
    vendorName,
    loading = false,
    onUpdate
}: DeveloperSettingsTableRowProps) => {
    const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const closeDialog = () => {
        setActiveDialog(null);
        setError(null);
    };

    const persist = async ({
        nextEnabled,
        nextVendorName,
        onSuccess
    }: {
        nextEnabled: boolean;
        nextVendorName: string;
        onSuccess?: () => void;
    }) => {
        setSaving(true);
        setError(null);
        try {
            await onUpdate({ enabled: nextEnabled, vendorName: nextVendorName });
            emitToast({ message: "Developer settings successfully updated." });
            onSuccess?.();
        } catch (err) {
            datadogRum.addError(err);
            setError(getErrorMessage(err));
            emitToast({ message: getErrorMessage(err), type: "bad-news" });
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = () => {
        if (saving) return;

        if (enabled) {
            // Disabling always requires confirmation.
            setActiveDialog("disable-confirm");
            return;
        }

        // Enabling for the first time (no vendor name yet) requires the vendor name modal.
        if (!vendorName) {
            setActiveDialog("set-vendor");
            return;
        }

        // Enabling with an existing vendor name: persist directly, no modal.
        persist({ nextEnabled: true, nextVendorName: vendorName });
    };

    const handleSaveVendorName = (newVendorName: string) => {
        // "set" flow enables the org; "edit" flow keeps the current enabled state.
        const nextEnabled = activeDialog === "set-vendor" ? true : enabled;
        persist({ nextEnabled, nextVendorName: newVendorName, onSuccess: closeDialog });
    };

    const handleConfirmDisable = () => {
        persist({ nextEnabled: false, nextVendorName: vendorName, onSuccess: closeDialog });
    };

    return (
        <Table.TR key={organizationId} data-testid={`developer-settings-row-${organizationId}`}>
            <Table.TD>{organizationName}</Table.TD>
            <Table.TD>
                <div className="flex align-items-center">
                    {loading || saving ? (
                        <Spinner />
                    ) : (
                        <Switch
                            elementId={`developer-toggle-${organizationId}`}
                            data-testid={`developer-toggle-${organizationId}`}
                            isDisabled={loading || saving}
                            checked={enabled}
                            onClick={handleToggle}
                        />
                    )}
                </div>
            </Table.TD>
            <Table.TD>{vendorName || "—"}</Table.TD>
            <Table.TD>
                {vendorName && (
                    <Button
                        data-testid={`edit-vendor-name-${organizationId}`}
                        size="small"
                        style="plain" //eslint-disable-line react/style-prop-object
                        isDisabled={saving}
                        onClick={() => {
                            setError(null);
                            setActiveDialog("edit-vendor");
                        }}
                    >
                        Edit name
                    </Button>
                )}
            </Table.TD>

            {(activeDialog === "set-vendor" || activeDialog === "edit-vendor") && (
                <VendorNameModal
                    mode={activeDialog === "edit-vendor" ? "edit" : "set"}
                    initialName={activeDialog === "edit-vendor" ? vendorName : ""}
                    loading={saving}
                    error={error}
                    onSave={handleSaveVendorName}
                    onCancel={closeDialog}
                />
            )}

            {activeDialog === "disable-confirm" && (
                <ConfirmationDialog
                    title="Disable developer mode"
                    callToActionText="Disable"
                    loading={saving}
                    error={error}
                    onCancel={closeDialog}
                    onConfirmation={handleConfirmDisable}
                >
                    <div key={1}>
                        Disabling developer mode will turn off developer features for{" "}
                        <span className="weight--bold">{organizationName}</span>. The vendor name is kept and will be
                        reused if developer mode is re-enabled.
                    </div>
                </ConfirmationDialog>
            )}
        </Table.TR>
    );
};

DeveloperSettingsTableRow.displayName = "DeveloperSettingsTableRow";
